export const HUBSPOT_MARKETPLACE_INSTALL_PATH = '/hubspot/install';
export const HUBSPOT_MARKETPLACE_PENDING_KEY = 'mb_hubspot_marketplace_install';

const PENDING_INSTALL_TTL_MS = 30 * 60 * 1000;
const DEFAULT_HUBSPOT_RETURN_HOSTS = [
  'app.hubspot.com',
  'app-eu1.hubspot.com',
] as const;

export interface PendingHubSpotMarketplaceInstall {
  returnUrl: string;
  expiresAt: number;
}

function getAllowedHubSpotReturnHosts(): Set<string> {
  const configured = (process.env.NEXT_PUBLIC_HUBSPOT_MARKETPLACE_RETURN_HOSTS || '')
    .split(',')
    .map((host) => host.trim().toLowerCase().replace(/\.$/, ''))
    .filter(Boolean);

  return new Set([...DEFAULT_HUBSPOT_RETURN_HOSTS, ...configured]);
}

/**
 * Canonicalize a HubSpot-owned return URL before it is stored or used for a
 * browser redirect. Exact host matching prevents open-redirect suffix tricks.
 */
export function validateHubSpotMarketplaceReturnUrl(rawUrl: string | null): string | null {
  if (!rawUrl || rawUrl.length > 2048) return null;

  try {
    const url = new URL(rawUrl);
    const hostname = url.hostname.toLowerCase().replace(/\.$/, '');
    const hasUnexpectedPort = Boolean(url.port && url.port !== '443');

    if (
      url.protocol !== 'https:'
      || !getAllowedHubSpotReturnHosts().has(hostname)
      || url.username
      || url.password
      || hasUnexpectedPort
    ) {
      return null;
    }

    url.hostname = hostname;
    url.hash = '';
    return url.toString();
  } catch {
    return null;
  }
}

export function buildHubSpotMarketplaceAuthorizePath(returnUrl: string): string {
  const trustedReturnUrl = validateHubSpotMarketplaceReturnUrl(returnUrl);
  if (!trustedReturnUrl) throw new Error('Invalid HubSpot Marketplace return URL');

  const params = new URLSearchParams({
    step: 'authorize',
    returnUrl: trustedReturnUrl,
  });
  return `${HUBSPOT_MARKETPLACE_INSTALL_PATH}?${params.toString()}`;
}

export function savePendingHubSpotMarketplaceInstall(
  storage: Pick<Storage, 'setItem'>,
  returnUrl: string,
  now = Date.now(),
): PendingHubSpotMarketplaceInstall | null {
  const trustedReturnUrl = validateHubSpotMarketplaceReturnUrl(returnUrl);
  if (!trustedReturnUrl) return null;

  const pending = {
    returnUrl: trustedReturnUrl,
    expiresAt: now + PENDING_INSTALL_TTL_MS,
  };
  storage.setItem(HUBSPOT_MARKETPLACE_PENDING_KEY, JSON.stringify(pending));
  return pending;
}

export function readPendingHubSpotMarketplaceInstall(
  storage: Pick<Storage, 'getItem' | 'removeItem'>,
  now = Date.now(),
): PendingHubSpotMarketplaceInstall | null {
  const rawPending = storage.getItem(HUBSPOT_MARKETPLACE_PENDING_KEY);
  if (!rawPending) return null;

  try {
    const candidate = JSON.parse(rawPending) as Partial<PendingHubSpotMarketplaceInstall>;
    const returnUrl = validateHubSpotMarketplaceReturnUrl(
      typeof candidate.returnUrl === 'string' ? candidate.returnUrl : null,
    );
    if (
      !returnUrl
      || typeof candidate.expiresAt !== 'number'
      || !Number.isFinite(candidate.expiresAt)
      || candidate.expiresAt <= now
      || candidate.expiresAt > now + PENDING_INSTALL_TTL_MS
    ) {
      storage.removeItem(HUBSPOT_MARKETPLACE_PENDING_KEY);
      return null;
    }

    return { returnUrl, expiresAt: candidate.expiresAt };
  } catch {
    storage.removeItem(HUBSPOT_MARKETPLACE_PENDING_KEY);
    return null;
  }
}

export function clearPendingHubSpotMarketplaceInstall(
  storage: Pick<Storage, 'removeItem'>,
): void {
  storage.removeItem(HUBSPOT_MARKETPLACE_PENDING_KEY);
}

export function isValidHubSpotMarketplaceState(state: string | null): state is string {
  return Boolean(state && state.length >= 32 && state.length <= 128 && /^[A-Za-z0-9_-]+$/.test(state));
}

/**
 * Redact the OAuth `code` and `state` query values from a Marketplace install
 * URL. HubSpot delivers both as query parameters at step=finalize, so any
 * consumer of page URLs (analytics, RUM, logs, history) must pass through this
 * before persisting or transmitting the URL. Non-install URLs pass unchanged.
 */
export function scrubHubSpotInstallSecretsFromUrl(rawUrl: string): string {
  if (!rawUrl || !rawUrl.includes(HUBSPOT_MARKETPLACE_INSTALL_PATH)) return rawUrl;

  try {
    const url = new URL(rawUrl, 'https://placeholder.invalid');
    if (!url.pathname.startsWith(HUBSPOT_MARKETPLACE_INSTALL_PATH)) return rawUrl;

    let changed = false;
    for (const key of ['code', 'state']) {
      if (url.searchParams.has(key)) {
        url.searchParams.set(key, 'REDACTED');
        changed = true;
      }
    }
    if (!changed) return rawUrl;
    return rawUrl.startsWith(HUBSPOT_MARKETPLACE_INSTALL_PATH)
      ? `${url.pathname}${url.search}${url.hash}`
      : url.toString();
  } catch {
    // A URL we cannot parse is a URL we cannot prove safe: drop the query entirely.
    return rawUrl.split('?')[0];
  }
}

